import type { LintDiagnostic } from '../../types/lint.js';
import type { CharterFrontmatter } from '../../types/charter.js';
import type { OrgChannelPolicy, ChannelId, SenderPolicyMode } from '../../types/channel.js';
import { getChannel } from '../../channels/registry.js';

/**
 * Channel lint rules:
 * - CHARTER.CHANNELS.UNKNOWN — channel ID not in registry
 * - CHARTER.CHANNELS.EMPTY_ALLOWLIST — allowlist policy but no channels
 * - CHARTER.CHANNELS.PII_ON_LIMITED — PII agent allows limited-tier channel
 * - CHARTER.CHANNELS.HIGH_RISK_PUBLIC — High risk agent allows High public exposure channel
 * - CHARTER.CHANNELS.PROD_DENYLIST — Prod uses denylist (prefer allowlist)
 * - CHARTER.CHANNELS.TEAM_CONFLICT — agent allows channel denied at org level
 */
export function runChannelRules(
  charter: CharterFrontmatter,
  orgPolicy?: OrgChannelPolicy,
): LintDiagnostic[] {
  const diagnostics: LintDiagnostic[] = [];
  const channels = charter.channels;

  // If no channels section in charter, skip all channel lint rules
  if (!channels) return diagnostics;

  // Check all channels are known
  const allDeclared = [...(channels.allowed ?? []), ...(channels.denied ?? [])];
  for (const channelId of allDeclared) {
    if (!getChannel(channelId)) {
      diagnostics.push({
        file: 'CHARTER.md',
        code: 'CHARTER.CHANNELS.UNKNOWN',
        path: `channels`,
        severity: 'error',
        message: `Channel "${channelId}" is not in the Augmented channel registry`,
      });
    }
  }

  // CHARTER.CHANNELS.EMPTY_ALLOWLIST
  if (channels.policy === 'allowlist' && (!channels.allowed || channels.allowed.length === 0)) {
    diagnostics.push({
      file: 'CHARTER.md',
      code: 'CHARTER.CHANNELS.EMPTY_ALLOWLIST',
      path: 'channels.allowed',
      severity: 'warning',
      message: 'Agent has allowlist policy but no channels listed (agent cannot receive messages)',
    });
  }

  // CHARTER.CHANNELS.PII_ON_LIMITED
  if (charter.risk_tier === 'High') {
    const effectiveChannels = channels.policy === 'allowlist' ? (channels.allowed ?? []) : [];
    for (const channelId of effectiveChannels) {
      const ch = getChannel(channelId);
      if (ch && ch.securityTier === 'limited') {
        diagnostics.push({
          file: 'CHARTER.md',
          code: 'CHARTER.CHANNELS.PII_ON_LIMITED',
          path: `channels.allowed`,
          severity: 'error',
          message: `High-risk agent allows "${channelId}" which is a limited-tier channel (no encryption guarantees)`,
        });
      }
    }
  }

  // CHARTER.CHANNELS.HIGH_RISK_PUBLIC
  if (charter.risk_tier === 'High') {
    const effectiveChannels = channels.policy === 'allowlist' ? (channels.allowed ?? []) : [];
    for (const channelId of effectiveChannels) {
      const ch = getChannel(channelId);
      if (ch && ch.publicExposureRisk === 'High') {
        diagnostics.push({
          file: 'CHARTER.md',
          code: 'CHARTER.CHANNELS.HIGH_RISK_PUBLIC',
          path: `channels.allowed`,
          severity: 'error',
          message: `High-risk agent allows "${channelId}" which has High public exposure risk`,
        });
      }
    }
  }

  // CHARTER.CHANNELS.PROD_DENYLIST
  if (charter.environment === 'prod' && channels.policy === 'denylist') {
    diagnostics.push({
      file: 'CHARTER.md',
      code: 'CHARTER.CHANNELS.PROD_DENYLIST',
      path: 'channels.policy',
      severity: 'warning',
      message: 'Production agent uses denylist channel policy (prefer explicit allowlist for prod)',
    });
  }

  // CHARTER.CHANNELS.TEAM_CONFLICT
  if (orgPolicy) {
    const agentAllowed = channels.policy === 'allowlist' ? (channels.allowed ?? []) : [];
    for (const channelId of agentAllowed) {
      if (orgPolicy.denied_channels.includes(channelId as ChannelId)) {
        diagnostics.push({
          file: 'CHARTER.md',
          code: 'CHARTER.CHANNELS.TEAM_CONFLICT',
          path: `channels.allowed`,
          severity: 'error',
          message: `Agent allows "${channelId}" but it is denied at org level`,
        });
      }
    }

    // Also check if org has an allowlist and agent channel is not in it
    if (orgPolicy.allowed_channels.length > 0) {
      const orgAllowed = new Set(orgPolicy.allowed_channels);
      for (const channelId of agentAllowed) {
        if (!orgAllowed.has(channelId as ChannelId)) {
          diagnostics.push({
            file: 'CHARTER.md',
            code: 'CHARTER.CHANNELS.TEAM_CONFLICT',
            path: `channels.allowed`,
            severity: 'error',
            message: `Agent allows "${channelId}" but it is not in the org allowlist`,
          });
        }
      }
    }

    // require_elevated_for_pii check
    if (orgPolicy.require_elevated_for_pii && charter.risk_tier === 'High') {
      const effectiveChannels = channels.policy === 'allowlist' ? (channels.allowed ?? []) : [];
      for (const channelId of effectiveChannels) {
        const ch = getChannel(channelId);
        if (ch && ch.securityTier !== 'elevated') {
          diagnostics.push({
            file: 'CHARTER.md',
            code: 'CHARTER.CHANNELS.PII_ON_LIMITED',
            path: `channels.allowed`,
            severity: 'error',
            message: `Org requires elevated channels for PII agents, but "${channelId}" is "${ch.securityTier}"-tier`,
          });
        }
      }
    }
  }

  // CHARTER.CHANNELS.SENDER_POLICY_CONFLICT
  // Agent's sender_policy must be at least as restrictive as the org's on
  // BOTH the human and agent axes (ENG-5842). A single-rank comparison no
  // longer fits because `manager_only` is stricter than `team_agents_only`
  // on humans (only the principal vs anyone) but equivalent on agents
  // (same-team labelled agents allowed in both).
  if (orgPolicy?.sender_policy) {
    const orgMode = orgPolicy.sender_policy.mode;
    // ENG-5842: when the agent has no explicit override, the runtime
    // resolver in /host/refresh treats it as "inherit the org default"
    // (the agent ends up running under the org's mode, by definition not
    // less restrictive). The pre-existing `?? 'all'` here flagged that
    // case as a violation against any restrictive org — out of sync with
    // runtime semantics. Skip the conflict check entirely when the agent
    // has no override; charter-side validation has nothing to flag.
    if (channels.sender_policy === undefined) {
      return diagnostics;
    }
    const agentMode = channels.sender_policy;
    const ranks = senderPolicyRanks();
    if (!(agentMode in ranks) || !(orgMode in ranks)) {
      // Don't silently treat unknown modes as the most permissive ("all")
      // — that would let a typo bypass an org policy. Surface it as a
      // conflict so the schema/validator finding stays visible.
      diagnostics.push({
        file: 'CHARTER.md',
        code: 'CHARTER.CHANNELS.SENDER_POLICY_CONFLICT',
        path: 'channels.sender_policy',
        severity: 'error',
        message: `Invalid sender_policy mode (agent="${agentMode}", org="${orgMode}")`,
      });
    } else {
      const a = ranks[agentMode as SenderPolicyMode]!;
      const o = ranks[orgMode as SenderPolicyMode]!;
      // Less restrictive on EITHER axis is a violation. The dimensions
      // compose: the agent must dominate the org on humans AND on agents.
      if (a.humanRank < o.humanRank || a.agentRank < o.agentRank) {
        diagnostics.push({
          file: 'CHARTER.md',
          code: 'CHARTER.CHANNELS.SENDER_POLICY_CONFLICT',
          path: 'channels.sender_policy',
          severity: 'error',
          message: `Agent sender_policy "${agentMode}" is less restrictive than the org policy "${orgMode}"`,
        });
      }
    }
  }

  return diagnostics;
}

/**
 * Per-axis restrictiveness ranks for SenderPolicyMode (ENG-5842).
 *
 * Two axes because the modes aren't a total order any more:
 *   - humanRank: how restrictive is this mode on inbound from humans?
 *       0 anyone, 1 no humans, 2 only the principal
 *   - agentRank: how restrictive is this mode on inbound from other
 *       Augmented agents?
 *       0 any agent, 1 same-team agents only
 *
 * Exported via senderPolicyRanks() so the per-axis check stays in lockstep
 * with downstream consumers (resolveEffectiveSenderPolicy in the API,
 * SENDER_POLICY_RANK in the webapp). When a new mode lands (e.g. ENG-5843's
 * internal_only composed flag, or any future axis), extend BOTH this table
 * and the webapp's rank in the same PR — drift between them silently
 * mis-warns the operator.
 */
export function senderPolicyRanks(): Record<SenderPolicyMode, { humanRank: number; agentRank: number }> {
  return {
    all: { humanRank: 0, agentRank: 0 },
    // ENG-5871: team_only sits between `all` and the human-drop modes —
    // admits a bounded set of N team-member humans (resolved at provision
    // time, see migration 20260602000003). Strictly less restrictive than
    // agents_only / team_agents_only / manager_only on humans, but on the
    // agent axis it matches team_agents_only (admits same-team agents
    // only, drops cross-team). Renumbering pushes the human-drop modes
    // from rank 1 to rank 2 and manager_only from rank 2 to rank 3 to
    // make room — monotonic in restrictiveness.
    team_only: { humanRank: 1, agentRank: 1 },
    // ENG-5871 renumber: was rank 1, now rank 2 (more restrictive than
    // team_only on humans — admits zero vs N).
    agents_only: { humanRank: 2, agentRank: 0 },
    team_agents_only: { humanRank: 2, agentRank: 1 },
    // ENG-5842 + ENG-5871 renumber: was rank 2, now rank 3. The single-rank
    // projection treats "named-one-principal" as semantically narrower
    // than "zero humans" — the existing convention from ENG-5842, kept
    // for cross-axis lint composition continuity. Known scalar-projection
    // limitation: org=manager_only + agent=agents_only fires a false
    // less-restrictive warning even though agents_only is stricter on the
    // human cardinality axis. Tracked for end-to-end per-axis fix in
    // ENG-5872 (PR B of the team_only work) — dropping the webapp's
    // single-rank SENDER_POLICY_RANK helper in favour of consuming this
    // per-axis table directly.
    manager_only: { humanRank: 3, agentRank: 1 },
  };
}
